import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BarChart3, Loader2, MessageCircle, Pause, Settings, Sparkles } from "lucide-react";
import ConfirmDialog, { buildDeleteDescription } from "../../components/tailwind/ConfirmDialog";
import { Button } from "../../components/tailwind/button";
import { Card, CardContent } from "../../components/tailwind/card";
import { Badge } from "../../components/tailwind/badge";
import { useToast } from "../../hooks/use-toast";
import { WorkflowActions } from "./components/WorkflowActions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/tailwind/dialog";
import {
  archiveWorkflowRequest,
  clearWorkflowArchiveState,
  deleteWorkflowRequest,
  clearWorkflowDeleteState,
  pauseWorkflowRequest,
  clearWorkflowPauseState,
  fetchBasicAutomationsRequest,
  fetchWorkflowListRequest
} from "../../store/slices/workflow";
import { BasicAutomationsPanel } from "./BasicAutomations";
import { WA_PAGE_PAD, WA_PAGE_GAP, waShell } from "./utils/whatsappPageShell";
import {
  getMaxVersion,
  getWorkflowListStatus
} from "./utils/workflowListUtils";
import { workflowTheme } from "./utils/workflowBuilderTheme";
import { usePageTitle } from "../../hooks/usePageTitle";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";
import { WaBone, WaCardBone } from "./components/skeletons/WhatsAppPageSkeletons";

const WORKFLOW_TABS = [
  { id: "my-workflows", label: "My Workflows" },
  { id: "basic-automations", label: "Basic Automations" }
];

const statusClass = {
  LIVE: `${workflowTheme.badgeLive} hover:bg-green-100 dark:hover:bg-green-900/30 border-none`,
  DRAFT: "bg-white text-gray-500 border-gray-300 dark:bg-slate-800 dark:text-gray-400 dark:border-slate-700",
  ARCHIVED: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-none dark:bg-yellow-900/30 dark:text-yellow-400",
  PAUSED: "bg-orange-100 text-orange-800 hover:bg-orange-100 border-none dark:bg-orange-900/30 dark:text-orange-400"
};

export default function WorkflowsList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workflows, loading, error } = useSelector((state) => state.workflowList || {});
  const workflowDelete = useSelector((state) => state.workflowDelete || {});
  const workflowArchive = useSelector((state) => state.workflowArchive || {});
  const workflowPause = useSelector((state) => state.workflowPause || {});
  const { toast } = useToast();
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [workflowToPause, setWorkflowToPause] = useState(null);

  const activeTab = searchParams.get("tab") === "basic-automations" ? "basic-automations" : "my-workflows";
  const basicAutomationsCreate = searchParams.get("create") === "1";
  usePageTitle(activeTab === "basic-automations" ? "Basic Automations" : "Workflows");

  const handleTabChange = (tabId) => {
    if (tabId === "basic-automations") {
      setSearchParams({ tab: "basic-automations" });
      return;
    }
    setSearchParams({});
  };

  useEffect(() => {
    dispatch(fetchWorkflowListRequest({ operation: "LIST_WORKFLOWS", payload: {} }));
    dispatch(fetchBasicAutomationsRequest({ operation: "GET_BASIC_AUTOMATIONS", payload: {} }));
  }, [dispatch]);

  useEffect(() => {
    if (workflowDelete?.response || workflowDelete?.error) {
      setWorkflowToDelete(null);
      dispatch(clearWorkflowDeleteState());
    }
  }, [workflowDelete, dispatch]);

  useEffect(() => {
    if (workflowPause?.response || workflowPause?.error) {
      setWorkflowToPause(null);
      dispatch(clearWorkflowPauseState());
    }
  }, [workflowPause, dispatch]);

  useEffect(() => {
    if (!workflowArchive?.response) return;
    toast({
      title: "Workflow Archived",
      description: workflowArchive?.response?.message || "Workflow archived successfully."
    });
    dispatch(clearWorkflowArchiveState());
    dispatch(fetchWorkflowListRequest({ operation: "LIST_WORKFLOWS", payload: {} }));
  }, [dispatch, toast, workflowArchive]);

  useEffect(() => {
    if (!workflowArchive?.error) return;
    toast({
      title: "Archive Failed",
      description: workflowArchive.error,
      variant: "destructive"
    });
    dispatch(clearWorkflowArchiveState());
  }, [dispatch, toast, workflowArchive?.error]);

  const stats = useMemo(() => {
    const total = workflows?.length || 0;
    const live = (workflows || []).filter((item) => getWorkflowListStatus(item) === "LIVE").length;
    const draft = (workflows || []).filter((item) => getWorkflowListStatus(item) === "DRAFT").length;
    return { total, live, draft };
  }, [workflows]);

  const handleDeleteClick = (event, workflow) => {
    event.stopPropagation();
    setWorkflowToDelete(workflow);
  };

  const handleDeleteConfirm = () => {
    if (!workflowToDelete) return;
    const version = workflowToDelete?.draftVersion || workflowToDelete?.activeVersion || workflowToDelete?.versions?.[0]?.version;
    if (!workflowToDelete?.workflowId || !version) return;
    dispatch(deleteWorkflowRequest({
      operation: "DELETE_WORKFLOW",
      payload: { workflowId: workflowToDelete.workflowId, version }
    }));
  };

  const handleCloseDeleteDialog = () => {
    if (workflowDelete?.loading) return;
    setWorkflowToDelete(null);
    dispatch(clearWorkflowDeleteState());
  };

  const handleArchive = (event, workflow) => {
    event.stopPropagation();
    const version = workflow?.draftVersion || workflow?.activeVersion || workflow?.versions?.[0]?.version;
    if (!workflow?.workflowId || !version) return;
    const isActive = !!workflow?.activeVersion;
    const payload = { workflowId: workflow.workflowId, version };
    if (isActive) payload.force = true;
    dispatch(archiveWorkflowRequest({
      operation: "ARCHIVE_WORKFLOW",
      payload
    }));
  };

  const handleClone = (event, workflow) => {
    event.stopPropagation();
    const version = workflow?.draftVersion || workflow?.activeVersion || workflow?.versions?.[0]?.version;
    navigate("/whatsapp/workflows/create/new", {
      state: {
        cloneOf: workflow.workflowId,
        version,
        workflowName: `${workflow.name || "Untitled workflow"} Clone`
      }
    });
  };

  const handlePauseClick = (event, workflow) => {
    event.stopPropagation();
    setWorkflowToPause(workflow);
  };

  const handlePauseConfirm = () => {
    if (!workflowToPause?.workflowId || !workflowToPause?.activeVersion) return;
    dispatch(pauseWorkflowRequest({
      operation: "PAUSE_WORKFLOW",
      payload: {
        workflowId: workflowToPause.workflowId,
        version: workflowToPause.activeVersion
      }
    }));
  };

  const handleClosePauseDialog = () => {
    if (workflowPause?.loading) return;
    setWorkflowToPause(null);
    dispatch(clearWorkflowPauseState());
  };

  return (
    <div className={waShell("w-full", WA_PAGE_PAD, WA_PAGE_GAP, "animate-fade-in", workflowTheme.page)}>
      <WhatsAppPageIntro
        description={
          activeTab === "basic-automations"
            ? "Simple rules-based replies that run without the visual canvas."
            : "Automate WhatsApp marketing, sales, and support with visual workflows."
        }
        actions={
          <>
            <button
              className="text-sm text-[#0F6E56] dark:text-emerald-400 font-medium hover:underline flex items-center"
              onClick={() => navigate("/whatsapp/workflows/analytics")}
            >
              Analytics <BarChart3 className="w-4 h-4 ml-1" />
            </button>
            <Button
              variant="outline"
              className="border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800"
              onClick={() => navigate("/whatsapp/workflows/settings")}
            >
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button className="!hidden bg-[#E1F5EE] dark:bg-emerald-950/40 text-[#085041] dark:text-emerald-300 hover:bg-[#CBECE1] dark:hover:bg-emerald-900/50 border-none">
              <Sparkles className="w-4 h-4 mr-2" /> Generate with AI
            </Button>
            <Button className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold" onClick={() => navigate("/whatsapp/workflows/create")}>
              + Create workflow
            </Button>
          </>
        }
        className="mb-2"
      />

      <div className={`flex border-b ${workflowTheme.tabBar} mb-8 overflow-x-auto`}>
        {WORKFLOW_TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${
              activeTab === id ? workflowTheme.tabActive : workflowTheme.tabInactive
            }`}
            onClick={() => handleTabChange(id)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className={activeTab === "my-workflows" ? undefined : "hidden"}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className={`${workflowTheme.card} shadow-sm rounded-xl`}><CardContent className="p-4"><p className={`text-xs font-semibold ${workflowTheme.textSecondary} uppercase tracking-wider`}>Total Workflows</p><h3 className={`text-2xl font-bold ${workflowTheme.textPrimary} mt-1`}>{stats.total}</h3></CardContent></Card>
        <Card className={`${workflowTheme.card} shadow-sm rounded-xl`}><CardContent className="p-4"><p className={`text-xs font-semibold ${workflowTheme.textSecondary} uppercase tracking-wider`}>Active (Live)</p><h3 className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{stats.live}</h3></CardContent></Card>
        <Card className={`${workflowTheme.card} shadow-sm rounded-xl`}><CardContent className="p-4"><p className={`text-xs font-semibold ${workflowTheme.textSecondary} uppercase tracking-wider`}>Draft</p><h3 className={`text-2xl font-bold ${workflowTheme.textPrimary} mt-1`}>{stats.draft}</h3></CardContent></Card>
        <Card className={`${workflowTheme.card} shadow-sm rounded-xl`}><CardContent className="p-4"><p className={`text-xs font-semibold ${workflowTheme.textSecondary} uppercase tracking-wider`}>Channel</p><h3 className="text-2xl font-bold text-[#0F6E56] dark:text-emerald-400 mt-1">WhatsApp</h3></CardContent></Card>
      </div>

      <div className="space-y-4">
        {loading && (
          <div className="space-y-3" aria-busy="true" aria-label="Loading workflows">
            {Array.from({ length: 4 }).map((_, i) => (
              <WaCardBone key={i} className="flex items-center gap-4">
                <WaBone className="h-12 w-12 rounded-full shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <WaBone className="h-4 w-48 max-w-full" />
                  <WaBone className="h-3 w-32 max-w-full" />
                </div>
                <WaBone className="h-8 w-20 rounded-lg shrink-0 hidden sm:block" />
              </WaCardBone>
            ))}
          </div>
        )}
        {!loading && error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
        {!loading && !error && (workflows || []).map((workflow) => {
          const status = getWorkflowListStatus(workflow);
          const maxVersion = getMaxVersion(workflow);
          const hasActivePublished = !!workflow.activeVersion;
          const showActiveVersion = hasActivePublished && workflow.activeVersion !== maxVersion?.version;
          return (
            <div
              key={workflow.workflowId}
              className={`${workflowTheme.cardHover} rounded-xl p-5 shadow-sm hover:shadow transition-shadow flex items-center cursor-pointer`}
              onClick={() =>
                navigate(`/whatsapp/workflows/${workflow.workflowId}/edit`, {
                  state: { version: workflow.draftVersion ?? workflow.activeVersion }
                })
              }
            >
              <div className="w-12 h-12 bg-green-50 dark:bg-green-950/40 rounded-full flex items-center justify-center shrink-0 border border-green-100 dark:border-green-900/50 mr-4">
                <MessageCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={`font-bold ${workflowTheme.textPrimary}`}>{workflow.name || "Untitled workflow"}</h3>
                  <Badge variant={status === "DRAFT" ? "outline" : "default"} className={statusClass[status]}>{status}</Badge>
                </div>
                <div className={`flex items-center gap-3 mt-1 text-xs ${workflowTheme.textMuted} font-medium`}>
                  <span>Latest Version: {maxVersion?.version ?? "-"}</span>
                  {showActiveVersion && (
                    <>
                      <span>·</span>
                      <span>Active Version: {workflow.activeVersion}</span>
                    </>
                  )}
                  <span>·</span>
                  <span>Updated: {maxVersion?.updated_at ? new Date(maxVersion.updated_at).toLocaleString() : "-"}</span>
                </div>
              </div>
              <WorkflowActions
                workflow={workflow}
                archiveLoading={workflowArchive.loading}
                pauseLoading={workflowPause.loading}
                deleteLoading={workflowDelete.loading}
                onClone={handleClone}
                onArchive={handleArchive}
                onPause={handlePauseClick}
                onDelete={handleDeleteClick}
              />
            </div>
          );
        })}
        {!loading && !error && !(workflows || []).length && (
          <div className={`text-sm ${workflowTheme.textSecondary}`}>No workflows found.</div>
        )}
      </div>
      </div>

      <div className={activeTab === "basic-automations" ? undefined : "hidden"}>
        <BasicAutomationsPanel embedded initialCreate={basicAutomationsCreate} />
      </div>

      <ConfirmDialog
        open={Boolean(workflowToDelete)}
        onOpenChange={(open) => {
          if (!open) handleCloseDeleteDialog();
        }}
        title="Delete workflow"
        description={buildDeleteDescription(
          workflowToDelete?.name,
          "Untitled workflow"
        )}
        confirmLabel="Delete workflow"
        loading={workflowDelete?.loading}
        loadingLabel="Deleting…"
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleDeleteConfirm}
      />

      <Dialog open={!!workflowToPause} onOpenChange={(open) => { if (!open) handleClosePauseDialog(); }}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(event) => {
          if (workflowPause?.loading) {
            event.preventDefault();
          }
        }}>
          <DialogHeader className="flex flex-col items-center sm:items-start text-center sm:text-left">
            <div className="w-12 h-12 rounded-full bg-orange-50 dark:bg-orange-950/30 flex items-center justify-center mb-4 text-orange-600 dark:text-orange-400">
              <Pause className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Pause workflow
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Pause <span className="font-semibold text-gray-900 dark:text-gray-100">&quot;{workflowToPause?.name || "Untitled workflow"}&quot;</span> v{workflowToPause?.activeVersion}? Its intent will be removed from inbound routing automatically. Override entries in trigger config for this intent may become stale.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClosePauseDialog}
              disabled={workflowPause?.loading}
              className="w-full sm:w-auto border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handlePauseConfirm}
              disabled={workflowPause?.loading}
              className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white border-none focus:ring-orange-500"
            >
              {workflowPause?.loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Pausing…
                </>
              ) : (
                "Pause workflow"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}